// Writing a GitLab issue body back out.
//
// THE CARRY RULE, and it is the whole module: a writer states only the sections
// it OWNS; everything else is carried verbatim, in its anchored place. Four
// writers used to rebuild the body from a private list of headings and drop
// whatever was not on it — which is why an Accept deleted a hand-written
// `## Notes`, why it deleted `## Context` that Loom's own chat-create had
// written, and why the same class of bug has been patched six times.
//
// ORDER IS FIXED (`KNOWN_ORDER`). That is what makes a body converge: the
// editor's Save and the refine Accept now write the same shape, so a ticket
// that has been through both settles instead of alternating, and the PATCH
// route's "the rendered base differs from the current base" test stops firing a
// write on every unchanged Save.

import {
  DOR_REASON_ONLY_LINE_RE,
  HTML_COMMENT_LINE_RE,
  KNOWN_ORDER,
  headingLangOfLine,
  headingOf,
  keyOfHeadingLine,
  renderDorReasonPayload,
  stripListMarker,
  type HeadingLang,
  type KnownKey,
} from "./tokens.js";
import {
  parseIssueBody,
  type BodySection,
  type ParsedIssueBody,
} from "./parse.js";

/**
 * What a writer claims.
 *
 *  - a string  — MINE: written under the canonical heading (in the language
 *                its copy in the body is written in, else `headingLang`'s);
 *  - `null`    — MINE AND EMPTY: the section is not written, so a copy the body
 *                carries is removed (a field the user emptied is a section the
 *                user removed);
 *  - absent    — NOT MINE: carried verbatim.
 */
export type OwnedSections = Partial<Record<KnownKey, string | null>>;

export interface RenderIssueBodyInput {
  /** The body being rewritten, as the parser read it. Branded: the ONLY way
   *  bytes are carried is through `parseIssueBody`. */
  readonly carried: ParsedIssueBody;
  readonly owned: OwnedSections;
  /** The story line / plain-text request above the first heading. Absent =
   *  carry. */
  readonly preamble?: string | null;
  /** Absent = carry. */
  readonly backlink?: string | null;
  /** The DoR-reason marker's PAYLOAD. Absent = carry the parsed one, `null` =
   *  none, a string = this one, rendered LAST. */
  readonly dorReason?: string | null;
  /**
   * The language of the ticket, for the heading of an owned section the body
   * does not have yet (`headingOf(key, lang)`: German only where `H_DE` has a
   * spelling). Absent: German when the body already spells one known heading
   * in German, else English. An owned section the body HAS keeps the
   * language its heading is written in, so a re-render moves no bytes.
   */
  readonly headingLang?: HeadingLang;
}

/** An empty body for a writer that composes from scratch (a split child, the
 *  create form, the chat-create draft). */
export const EMPTY_BODY: ParsedIssueBody = parseIssueBody("");

/**
 * Owned content, with a canonical heading line stripped off the front.
 *
 * A marked block is owned as content FROM ITS BEGIN MARKER, but the two
 * composers that produce one (`mergeDesignRefs`, the build-notes composer)
 * return a heading-led block — `mergeDesignRefs` returns the existing block
 * verbatim, heading included, when it cannot re-parse a row. Stripping here
 * rather than at each call site is what keeps the heading from being written
 * twice by whichever caller forgets.
 */
const ownedContentOf = (key: KnownKey, content: string): string => {
  const lines = content.split("\n");
  const first = (lines[0] ?? "").trim();
  if (keyOfHeadingLine(first) === key) {
    return lines.slice(1).join("\n").trim();
  }
  return content.trim();
};

/**
 * What a section SAYS: each non-blank line trimmed and without its list marker.
 *
 * So a Definition of Done pasted into the Description as `1. …` rows matches
 * the same rows written as `- …`; a marker line is not content. The one
 * comparison it feeds: an earlier copy of a heading whose content the owned
 * text already holds is not written twice.
 */
const fingerprintOf = (text: string): string =>
  text
    .split("\n")
    .map((line) => stripListMarker(line))
    .filter(
      (line) =>
        line.length > 0 &&
        !DOR_REASON_ONLY_LINE_RE.test(line) &&
        !HTML_COMMENT_LINE_RE.test(line),
    )
    .join("\n");

/**
 * The keys whose content opens with an HTML marker rather than prose.
 *
 * They get `heading\ncontent`, not `heading\n\ncontent`. The blank line is for
 * a section a person reads and writes; every composer of a marked block
 * (`renderDesignSection`, `composeChildDescription`, `renderFromMeetingBlock`,
 * `renderFromMailBlock`) puts the BEGIN marker on the line directly under the heading, and matching
 * them byte-for-byte means
 * the renderer moves no bytes on a ticket that already carries one.
 */
const TIGHT_HEADING: ReadonlySet<KnownKey> = new Set<KnownKey>([
  "build_notes",
  "design",
  "from_mail",
  "from_meeting",
]);

/** The copy of a repeated heading the renderer puts in the slot: the last one
 *  with content, else the last one. `sectionOf`'s rule, over one key's copies
 *  — kept here so render and parse cannot drift. */
const slotCopy = (copies: readonly BodySection[]): BodySection | null => {
  if (copies.length === 0) return null;
  const withBody = copies.filter((s) => s.body.trim().length > 0);
  return (withBody[withBody.length - 1] ?? copies[copies.length - 1]) ?? null;
};

/**
 * What the OWNED text of this render says, key by key.
 *
 * Owned content is markdown, and markdown can carry a heading: a user who
 * pastes a Definition of Done into the editor's Description field puts
 * `## Definition of Done` inside `what_you_need`'s content. The carried copy of
 * that section then says the same thing, and writing both is the duplicate this
 * set exists to stop. So the owned content is PARSED, and every section it
 * turns out to contain counts as owned-says — not just the slot it was handed
 * to.
 */
const ownedFingerprints = (owned: OwnedSections): ReadonlySet<string> => {
  const says = new Set<string>();
  for (const key of KNOWN_ORDER) {
    const raw = owned[key];
    if (raw === undefined || raw === null || raw.trim().length === 0) continue;
    const content = ownedContentOf(key, raw);
    says.add(`${key}\n${fingerprintOf(content)}`);
    const inner = parseIssueBody(`${headingOf(key)}\n\n${content}`);
    for (const s of inner.sections) {
      if (s.key === null) continue;
      says.add(`${s.key}\n${fingerprintOf(s.body)}`);
    }
  }
  return says;
};

/**
 * A writer's own preamble, split into the text above the first heading and the
 * sections it turns out to carry.
 *
 * THE PREAMBLE IS MARKDOWN, and markdown can carry a heading. The editor's
 * Description box is one plain textarea: a requester who types
 * `## Definition of Done` into it hands this renderer a "preamble" holding a
 * section. Pushed verbatim, that heading is only a section on the NEXT parse,
 * and the save after it RELOCATES the heading into its canonical slot — so an
 * unchanged Save wrote a different body than the one before it, costing one
 * extra GitLab write and one extra DoR re-check per affected ticket. Parsing
 * here is what makes the FIRST write the fixed point.
 *
 * It is also a no-op in the ordinary case, twice over: a preamble no writer
 * supplied is `carried.preamble`, which the parser has already cut at the first
 * heading, and a supplied one that holds no heading parses to itself.
 *
 * THE TWO GUARDS are about bytes, not convergence. A `**Acceptance criteria**`
 * label block is lifted into a synthesised section only when the body carries
 * no `## Acceptance criteria` — true of a preamble read on its own, false of
 * the body it came from — so parsing one in isolation would mint a second
 * criteria section out of a block the whole-body parse deliberately left as
 * prose. A `_Part of #N_` line is lifted out entirely, and this renderer writes
 * back only the backlink it was handed. In both cases the preamble rides
 * verbatim, exactly as it did before.
 */
const splitPreamble = (
  raw: string,
): { readonly text: string; readonly sections: readonly BodySection[] } => {
  const parsed = parseIssueBody(raw);
  if (
    parsed.sections.length === 0 ||
    parsed.legacyCriteria !== null ||
    parsed.backlink !== null
  ) {
    return { text: raw, sections: [] };
  }
  return { text: parsed.preamble, sections: parsed.sections };
};

/**
 * The language a body's NEW sections are headed in: the writer's, else German
 * when one known heading the body carries is spelled in German, else English.
 */
const newHeadingLangOf = (
  sections: readonly BodySection[],
  stated: HeadingLang | undefined,
): HeadingLang => {
  if (stated !== undefined) return stated;
  return sections.some((s) => headingLangOfLine(s.heading) === "de") ? "de" : "en";
};

export const renderIssueBody = (input: RenderIssueBodyInput): string => {
  const { carried, owned } = input;
  const ownedSays = ownedFingerprints(owned);
  const parts: string[] = [];

  const backlink =
    "backlink" in input ? (input.backlink ?? null) : carried.backlink;
  if (backlink !== null && backlink.trim().length > 0) parts.push(backlink);

  const preamble = splitPreamble(
    "preamble" in input ? (input.preamble ?? "") : carried.preamble,
  );
  if (preamble.text.trim().length > 0) parts.push(preamble.text.trim());

  // A section the preamble carried sat ABOVE everything the body carried, so it
  // goes in front: `slotCopy` takes the LAST copy with content, which keeps the
  // body's own section in the slot and renders the typed one before it —
  // the same shape the second write used to produce, one write earlier.
  const sections: readonly BodySection[] =
    preamble.sections.length === 0
      ? carried.sections
      : [...preamble.sections, ...carried.sections];

  const unknownsAt = (anchor: KnownKey | "preamble"): BodySection[] =>
    sections.filter((s) => s.key === null && s.anchor === anchor);

  for (const s of unknownsAt("preamble")) parts.push(s.raw);

  const newLang = newHeadingLangOf(sections, input.headingLang);
  /** The owned section's heading: the language the body's copy is written
   *  in, else the new sections' one. */
  const ownedHeadingOf = (key: KnownKey, slot: BodySection | null): string =>
    headingOf(key, (slot === null ? null : headingLangOfLine(slot.heading)) ?? newLang);

  for (const key of KNOWN_ORDER) {
    const copies = sections.filter((s) => s.key === key);
    const slot = slotCopy(copies);
    const isOwned = Object.prototype.hasOwnProperty.call(owned, key);
    const raw = isOwned ? (owned[key] ?? null) : null;
    const content =
      raw === null || raw.trim().length === 0 ? null : ownedContentOf(key, raw);

    // OWNING A KEY CLAIMS EVERY COPY OF IT. A writer that states
    // `acceptance_criteria` is the authority on that heading, so a second
    // `## Acceptance criteria` the body happens to carry is replaced rather
    // than kept above the fresh one: kept, it renders stale-then-fresh, the
    // editor shows only the last copy, and the pair is idempotent — so the
    // stale list is permanent and the user cannot see or delete it. No live
    // body has a duplicate known heading today (all 60 newest checked), which
    // is what makes this the moment to settle the rule rather than discover it.
    //
    // Everything NOT owned still renders verbatim immediately before the slot,
    // so last-copy-wins stays stable across a re-parse — unless it says exactly
    // what the owned text of this render says, which is the "DoD pasted into
    // the Description" rule.
    for (const copy of copies) {
      if (copy === slot) continue;
      if (isOwned) continue;
      if (ownedSays.has(`${key}\n${fingerprintOf(copy.body)}`)) continue;
      parts.push(copy.raw);
    }
    if (isOwned) {
      if (content !== null) {
        parts.push(
          `${ownedHeadingOf(key, slot)}${TIGHT_HEADING.has(key) ? "\n" : "\n\n"}${content}`,
        );
      }
    } else if (
      slot !== null &&
      !ownedSays.has(`${key}\n${fingerprintOf(slot.body)}`)
    ) {
      // Not owned at this key, but the owned text of some OTHER key already
      // says it — the pasted-Definition-of-Done case again, where the carried
      // section is the slot rather than an earlier copy.
      parts.push(slot.raw);
    }
    for (const s of unknownsAt(key)) parts.push(s.raw);
  }

  const dorReason =
    "dorReason" in input ? (input.dorReason ?? null) : carried.dorReason;
  const out = parts.filter((p) => p.trim().length > 0).join("\n\n");
  if (dorReason === null || dorReason.trim().length === 0) return out;
  const marker = renderDorReasonPayload(dorReason.trim());
  return out.length > 0 ? `${out}\n\n${marker}` : marker;
};
